import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Grid,
  GridItem,
  Table,
  Badge,
  Checkbox,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@chakra-ui/react'
import { AlertCircle, Check, CreditCard } from 'lucide-react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import {
  useCreateSupplierPayment,
  useSuppliers,
  useUnpaidSupplierInvoices,
  useResidenceChartAccounts,
} from '../hooks'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { SupplierPaymentMode } from '../types'
import { SUPPLIER_PAYMENT_MODE_LABELS } from '../types'

interface SupplierPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  fiscalYearId: string
  preSelectedSupplierId?: string
}

type InvoiceAllocation = {
  invoice_id: string
  invoice_number: string
  internal_reference: string
  invoice_date: string
  due_date: string
  total_ttc: number
  amount_remaining: number
  selected: boolean
  amount_to_pay: number
}

type FormData = {
  supplier_id: string
  payment_date: string
  payment_mode: SupplierPaymentMode
  bank_account_id: string
  reference: string
}

const getToday = (): string => new Date().toISOString().split('T')[0] as string

const initialFormData: FormData = {
  supplier_id: '',
  payment_date: getToday(),
  payment_mode: 'VIREMENT',
  bank_account_id: '',
  reference: '',
}

export function SupplierPaymentModal({
  isOpen,
  onClose,
  residenceId,
  fiscalYearId,
  preSelectedSupplierId,
}: SupplierPaymentModalProps) {
  const { data: authData } = useAuth()
  const createPayment = useCreateSupplierPayment()

  const { data: suppliers } = useSuppliers({
    agency_id: authData?.profile?.agency_id || '',
    is_active: true,
  })

  const { data: residenceAccounts } = useResidenceChartAccounts(residenceId)

  // Filtrer les comptes bancaires (classe 5)
  const bankAccounts = residenceAccounts?.filter(
    (a) => a.chart_account?.code?.startsWith('5') && a.is_active
  )

  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [allocations, setAllocations] = useState<InvoiceAllocation[]>([])

  // Charger les factures impayées du fournisseur
  const { data: unpaidInvoices, isLoading: invoicesLoading } = useUnpaidSupplierInvoices(
    residenceId,
    formData.supplier_id || undefined
  )

  // Initialiser avec le fournisseur pré-sélectionné
  useEffect(() => {
    if (isOpen && preSelectedSupplierId) {
      setFormData((prev) => ({ ...prev, supplier_id: preSelectedSupplierId }))
    }
  }, [isOpen, preSelectedSupplierId])

  // Mettre à jour les allocations quand les factures changent
  useEffect(() => {
    if (unpaidInvoices) {
      setAllocations(
        unpaidInvoices.map((inv) => ({
          invoice_id: inv.id,
          invoice_number: inv.invoice_number,
          internal_reference: inv.internal_reference,
          invoice_date: inv.invoice_date,
          due_date: inv.due_date,
          total_ttc: inv.total_ttc,
          amount_remaining: inv.amount_remaining,
          selected: false,
          amount_to_pay: inv.amount_remaining,
        }))
      )
    } else {
      setAllocations([])
    }
  }, [unpaidInvoices])

  // Calcul du total à payer
  const totalToPay = useMemo(() => {
    return allocations
      .filter((a) => a.selected)
      .reduce((sum, a) => sum + a.amount_to_pay, 0)
  }, [allocations])

  const hasValidAllocations = allocations.some((a) => a.selected && a.amount_to_pay > 0)

  const handleClose = () => {
    setFormData(initialFormData)
    setAllocations([])
    onClose()
  }

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Reset allocations when supplier changes
    if (field === 'supplier_id') {
      setAllocations([])
    }
  }

  const handleToggleInvoice = (invoiceId: string) => {
    setAllocations((prev) =>
      prev.map((a) => (a.invoice_id === invoiceId ? { ...a, selected: !a.selected } : a))
    )
  }

  const handleAmountChange = (invoiceId: string, amount: number) => {
    setAllocations((prev) =>
      prev.map((a) =>
        a.invoice_id === invoiceId
          ? { ...a, amount_to_pay: Math.min(Math.max(0, amount), a.amount_remaining) }
          : a
      )
    )
  }

  const handleSelectAll = () => {
    const allSelected = allocations.every((a) => a.selected)
    setAllocations((prev) => prev.map((a) => ({ ...a, selected: !allSelected })))
  }

  const handleSubmit = async () => {
    if (!formData.supplier_id) {
      toaster.create({
        title: 'Erreur',
        description: 'Le fournisseur est obligatoire',
        type: 'error',
      })
      return
    }

    if (!formData.bank_account_id) {
      toaster.create({
        title: 'Erreur',
        description: 'Le compte bancaire est obligatoire',
        type: 'error',
      })
      return
    }

    if (!hasValidAllocations) {
      toaster.create({
        title: 'Erreur',
        description: 'Veuillez sélectionner au moins une facture',
        type: 'error',
      })
      return
    }

    try {
      const selectedAllocations = allocations
        .filter((a) => a.selected && a.amount_to_pay > 0)
        .map((a) => ({
          invoice_id: a.invoice_id,
          amount: Math.round(a.amount_to_pay * 100) / 100,
        }))

      await createPayment.mutateAsync({
        residenceId,
        fiscalYearId,
        supplierId: formData.supplier_id,
        paymentDate: formData.payment_date,
        paymentMode: formData.payment_mode,
        bankAccountId: formData.bank_account_id,
        allocations: selectedAllocations,
        reference: formData.reference.trim() || undefined,
      })

      toaster.create({
        title: 'Succès',
        description: 'Règlement créé avec succès',
        type: 'success',
      })

      handleClose()
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : "Erreur lors de l'enregistrement",
        type: 'error',
      })
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR')
  }

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date()
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="xl">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="900px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Nouveau règlement fournisseur</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {/* Informations générales */}
            <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={6}>
              <GridItem colSpan={2}>
                <Field label="Fournisseur" required>
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.supplier_id}
                      onChange={(e) => handleChange('supplier_id', e.target.value)}
                    >
                      <option value="">Sélectionner un fournisseur</option>
                      {suppliers?.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.code} - {s.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Date de règlement" required>
                  <Input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => handleChange('payment_date', e.target.value)}
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Mode de paiement" required>
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.payment_mode}
                      onChange={(e) =>
                        handleChange('payment_mode', e.target.value as SupplierPaymentMode)
                      }
                    >
                      {Object.entries(SUPPLIER_PAYMENT_MODE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Compte bancaire" required>
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.bank_account_id}
                      onChange={(e) => handleChange('bank_account_id', e.target.value)}
                    >
                      <option value="">Sélectionner un compte</option>
                      {bankAccounts?.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.custom_code || a.chart_account?.code} -{' '}
                          {a.custom_name || a.chart_account?.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Référence">
                  <Input
                    value={formData.reference}
                    onChange={(e) => handleChange('reference', e.target.value)}
                    placeholder="N° de chèque, référence virement..."
                  />
                </Field>
              </GridItem>
            </Grid>

            {/* Liste des factures à régler */}
            {formData.supplier_id && (
              <Box>
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontWeight="medium">Factures à régler</Text>
                  {allocations.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                      {allocations.every((a) => a.selected) ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </Button>
                  )}
                </Flex>

                {invoicesLoading ? (
                  <Box p={4} textAlign="center" bg="gray.50" borderRadius="md">
                    <Text color="gray.500">Chargement des factures...</Text>
                  </Box>
                ) : allocations.length === 0 ? (
                  <Box p={4} textAlign="center" bg="gray.50" borderRadius="md">
                    <CreditCard
                      size={32}
                      color="var(--chakra-colors-gray-300)"
                      style={{ margin: '0 auto' }}
                    />
                    <Text color="gray.500" mt={2}>
                      Aucune facture impayée pour ce fournisseur
                    </Text>
                  </Box>
                ) : (
                  <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row bg="gray.50">
                          <Table.ColumnHeader w="40px"></Table.ColumnHeader>
                          <Table.ColumnHeader>N° Facture</Table.ColumnHeader>
                          <Table.ColumnHeader>Date</Table.ColumnHeader>
                          <Table.ColumnHeader>Échéance</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right">Total TTC</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right">Reste dû</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right" w="120px">
                            Montant à payer
                          </Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {allocations.map((alloc) => (
                          <Table.Row
                            key={alloc.invoice_id}
                            bg={alloc.selected ? 'blue.50' : undefined}
                          >
                            <Table.Cell>
                              <Checkbox.Root
                                checked={alloc.selected}
                                onCheckedChange={() => handleToggleInvoice(alloc.invoice_id)}
                              >
                                <Checkbox.HiddenInput />
                                <Checkbox.Control>
                                  <Checkbox.Indicator />
                                </Checkbox.Control>
                              </Checkbox.Root>
                            </Table.Cell>
                            <Table.Cell>
                              <Text fontWeight="medium">{alloc.invoice_number}</Text>
                              <Text fontSize="xs" color="gray.500">
                                {alloc.internal_reference}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>{formatDate(alloc.invoice_date)}</Table.Cell>
                            <Table.Cell>
                              <Flex align="center" gap={1}>
                                <Text>{formatDate(alloc.due_date)}</Text>
                                {isOverdue(alloc.due_date) && (
                                  <Badge colorPalette="red" size="sm">
                                    En retard
                                  </Badge>
                                )}
                              </Flex>
                            </Table.Cell>
                            <Table.Cell textAlign="right" fontFamily="mono">
                              {formatCurrency(alloc.total_ttc)}
                            </Table.Cell>
                            <Table.Cell textAlign="right" fontFamily="mono" fontWeight="medium">
                              {formatCurrency(alloc.amount_remaining)}
                            </Table.Cell>
                            <Table.Cell>
                              <Input
                                size="sm"
                                type="number"
                                step="0.01"
                                min="0"
                                max={alloc.amount_remaining}
                                textAlign="right"
                                value={alloc.amount_to_pay || ''}
                                onChange={(e) =>
                                  handleAmountChange(alloc.invoice_id, parseFloat(e.target.value) || 0)
                                }
                                disabled={!alloc.selected}
                                fontFamily="mono"
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))}

                        {/* Ligne total */}
                        <Table.Row bg="gray.200">
                          <Table.Cell colSpan={6} textAlign="right" fontWeight="bold">
                            Total à payer
                          </Table.Cell>
                          <Table.Cell textAlign="right" fontWeight="bold" fontFamily="mono" fontSize="md">
                            {formatCurrency(totalToPay)}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table.Root>
                  </Box>
                )}
              </Box>
            )}

            {/* Indicateur de validation */}
            <Flex mt={4} justify="flex-end">
              {hasValidAllocations ? (
                <Badge colorPalette="green" size="lg">
                  <Check size={14} />
                  Règlement prêt : {formatCurrency(totalToPay)}
                </Badge>
              ) : formData.supplier_id ? (
                <Badge colorPalette="orange" size="lg">
                  <AlertCircle size={14} />
                  Sélectionnez des factures à régler
                </Badge>
              ) : null}
            </Flex>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={createPayment.isPending}
              disabled={
                !formData.supplier_id ||
                !formData.bank_account_id ||
                !hasValidAllocations
              }
            >
              Créer le règlement
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
